import type { PanelInfo, PanelData } from '../../types/monitoringTypes';
import Table from '../Table';

import type { TableField, TableRowData } from '@/types/tableTypes';

interface Field {
  id: string;
  title: string;
}

interface PanelValues {
  fields: TableField[];
  data: TableRowData[] | undefined;
}

interface MonitoringTableProps {
  panelInfo: PanelInfo;
  styles: { height: string | number };
  isLoading: boolean;
  panelData?: PanelData;
}

const mapTableData = (panelData: Record<string, unknown>[], fields: Field[]): TableRowData[] =>
  panelData.map((row, index) => ({
    key: index.toString(),
    data: fields.map(field => {
      if (typeof row[field.id] === 'number') {
        return {
          type: 'progressBar',
          field: field.id,
          value: Math.floor((row[field.id] as number) * 1000) / 1000,
        };
      }
      return {
        field: field.id,
        value: row[field.id] as string | number,
      };
    }),
  }));

const MonitoringTable = ({ panelInfo, styles, isLoading, panelData }: MonitoringTableProps) => {
  const getPanelValues = (): PanelValues => {
    if (!panelData || Object.keys(panelData).length === 0) {
      return { fields: [], data: [] };
    }

    try {
      const { refId } = panelInfo.targets?.find(target => !target.hide) ?? {};

      const frames =
        refId && panelData.results[refId] && 'frames' in panelData.results[refId]
          ? (
              panelData.results[refId] as {
                frames?: { data: { values: unknown[] }; schema?: { fields: { name: string }[] } }[];
              }
            ).frames?.[0]
          : undefined;

      if (!frames?.data.values) {
        return { fields: [], data: [] };
      }

      const transformations = panelInfo.transformations?.find(
        transformation => transformation.id === 'organize'
      )?.options;

      const formatedData = (frames.data.values[0] as unknown[]).map((_: unknown, i: number) =>
        (frames.data.values as unknown[][])
          .map((row: unknown[]) => row[i])
          .reduce<Record<string, unknown>>((acc, v, index) => {
            acc[frames.schema?.fields[index]?.name ?? ''] = v;
            return acc;
          }, {})
      );

      const fields: TableField[] = transformations
        ? Object.keys(transformations.renameByName ?? {})
            .map(key => ({
              id: key,
              title: transformations.renameByName?.[key] ?? key,
              type: 'default',
            }))
            .sort(
              (a, b) =>
                (transformations.indexByName?.[a.id] ?? 0) -
                (transformations.indexByName?.[b.id] ?? 0)
            )
        : [];

      return {
        fields,
        data: mapTableData(formatedData, fields),
      };
    } catch (error) {
      console.error('Error while getting panel values:', error);
      return { fields: [], data: [] };
    }
  };

  const value = getPanelValues();

  return (
    <div style={{ height: styles.height, overflowY: 'scroll' }}>
      <Table
        fields={value.fields}
        rows={value.data ?? []}
        styles={{ height: styles.height }}
        search={false}
        pagination={false}
        pageSize={value.data?.length ?? 10}
        isLoading={isLoading}
      />
    </div>
  );
};

export default MonitoringTable;
